// gamehost.ts — the GAME HOST: owns the game-session lifecycle, the launch
// seam (ggame.PlayLevel / PlaySavedGame / RunSimulation), the pump that
// switches between the running game and the menu, and the MP wiring
// (lobby deps + mid-game reconnect). It is the durable substrate the menu
// drives: a menu (the canvas ShellSession today; a DOM shell in a later phase
// of tasks/html-ui-shell-plan.md) calls launchSinglePlayer/launchSavedGame/
// runSimulation and the host runs the game, reporting the knGo* outcome back.
//
// Extracted from web/main.ts (HTML/DOM UI shell plan, Phase 0) WITHOUT behavior
// change: the orchestration is identical, only the module globals became `this`
// fields and the browser-only seams (httpGet, spEvent, key-state poll) are
// injected — so the host is node-testable (test/client) exactly like the
// sessions it hosts. The browser glue (canvas/GL present, rAF loop, pointer,
// resize, audio unlock, the DLC/webview/ask host seams, the wiTs handle) stays
// in main.ts and drives this host.

import { Sim } from '../../../sim/src/index.ts';
import type { IniSection, AnirJson } from '../../../sim/src/index.ts';
import type { Stream } from '../../../sim/src/sm/stream.ts';
import type { IniReader } from '../../../sim/src/data/ini.ts';
import { LevelDataFromPack } from '../io/levelpack.ts';
import { PlaySavedGame, type MissionIdentifier, type SaveStore } from '../io/savegame.ts';
import { type PrefStore, kszPrefDeviceId, kfHcapEasy, kfHcapHard } from '../io/prefs.ts';
import type { CompleteManager } from '../io/completemgr.ts';
import type { PackFileReader } from '../io/packfile.ts';
import type { TexAtlasMgr } from '../gfx/texatlasmgr.ts';
import type { CoordMap } from '../gfx/coords.ts';
import type { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import type { ShellSession } from '../ui/shell.ts';
import { GameSession } from '../ui/gamesession.ts';
import type { InGameMenuButton } from '../ui/gameoptions.ts';
import type { ObjectivesSnapshot } from '../dom/ingame/objectives-snapshot.ts';
import type { SimUIModel } from '../dom/ingame/simui-view.ts';
import type { GameOptionsModel } from '../dom/ingame/game-options-io.ts';
import type { ObjectivesSummaryInfo } from '../ui/objectives.ts';
import type { EcomInfo } from '../ui/ecom.ts';
import type { CutSceneInfo } from '../ui/cutscene.ts';
import type { UnitMenuModel, BuildPanelModel } from '../ui/buildui.ts';
import { PlayLevelSession, WireJumpToMission, clientFogHost } from '../ui/playlevel.ts';
import {
  knGoSuccess,
  knGoInitFailure,
  knGoAbortLevel,
  knGoRetryLevel,
  knGoAppStop,
  knGoLoadSavedGame,
} from '../ui/event.ts';
import { XTransport, webSocketConnector, XTS } from '../net/xtransport.ts';
import type { BeginGameInfo, LobbyShellDeps } from '../ui/lobby.ts';
import type { MissionListDeps } from '../ui/missionpick.ts';
import type { HttpPackManager } from '../net/httppackmanager.ts';
import type { HttpPackInfoManager } from '../net/httppackinfomanager.ts';
import { NMID, CLIENT_TS } from '../../../protocol/constants.ts';

interface RunningGame {
  gs: GameSession;
  onGo: (nGo: number) => void;
}
type PvarEntry = { name: string; value: string };

/** The browser-MP lockstep observability the E2E harness reads (wiTs.mpStats). */
export interface MpStats {
  live: boolean;
  reports: number;
  syncErrors: number;
  hashes: [number, number][];
}

/** Everything the host needs that is NOT the menu (which is attach()ed). The
 *  shared art stack + sim data, the persistent stores + content managers, the
 *  MP transport holder + chat controller, and the browser-only seams the host
 *  cannot own itself (key-state poll, http GET, SP telemetry). */
export interface GameHostDeps {
  // the shared art stack (one pakr/tam/coords for the menu and every session)
  pakr: PackFileReader;
  tam: TexAtlasMgr;
  coords: CoordMap;
  gameIni: IniReader;
  screen: { cx: number; cy: number };
  /** Renderer seam: the DibBitmap surface factory threaded to each session. */
  createSurface?: (cx: number, cy: number) => CpuDibBitmap;
  // the sim data tables
  templates: IniSection[];
  anir: AnirJson;
  // persistence + content
  prefStore: PrefStore;
  saveStore: SaveStore;
  completeMgr: CompleteManager;
  packm: HttpPackManager;
  ppim: HttpPackInfoManager;
  // multiplayer
  mp: { tra: XTransport | null };
  chatc: LobbyShellDeps['chatc'];
  /** ?api override; undefined = prod kszServerInfoUrl. */
  serverInfoUrl?: string;
  /** ?beta — show non-production servers. */
  envBeta: boolean;
  /** SimpleRequest::Get over the host (the serverinfo/auth GETs). */
  httpGet: (url: string) => Promise<{ body: string | null; error?: string }>;
  // host seams the sessions need
  /** InputUIForm's held-arrow-key scroll poll (HostGetCurrentKeyState). */
  hostGetCurrentKeyState: (keyBits: number) => number;
  /** SP funnel telemetry sink (anonymous, fire-and-forget; node = no-op). */
  spEvent: (body: Record<string, unknown>) => void;
  /** The in-game OBJECTIVES briefing
   *  DOM-overlay seam threaded into each GameSession (gameSessionOpts.domShowObjectives).
   *  main.ts mounts an ObjectivesView over the canvas root and calls done() on OK.
   *  Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domShowObjectives?: (snapshot: ObjectivesSnapshot, done: () => void) => void;
  /** The in-game PAUSE MENU (Form 2)
   *  DOM-overlay seam threaded into each GameSession (gameSessionOpts.domInGameMenu).
   *  main.ts mounts a PauseMenuView over the canvas root; onSelect routes the chosen
   *  kidc through the shared handler. Absent (a headless harness) ⇒ no UI shown. */
  domInGameMenu?: (buttons: InGameMenuButton[], onSelect: (idc: number) => void) => void;
  /** The WIN / LOSE RESULTS (Form 3)
   *  SUMMARY DOM-overlay seam threaded into each GameSession
   *  (gameSessionOpts.domObjectivesSummary). main.ts mounts an ObjectivesView (kind
   *  win/lose) over the canvas root; onChoice routes the chosen kidc through the
   *  shared handler. Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domObjectivesSummary?: (info: ObjectivesSummaryInfo, onChoice: (idc: number) => void) => void;
  /** MP: fired when the server announces (ScReplayAvailable) a just-won game's recording is in R2, so the
   *  end-of-game summary can light up "VIEW REPLAY". Threaded into each GameSession. Absent ⇒ ignored. */
  onReplayAvailable?: (replayKey: string) => void;
  /** The in-game GAME OPTIONS (Form 4)
   *  DOM-overlay seam threaded into each GameSession (gameSessionOpts.domGameOptions).
   *  main.ts mounts a GameOptionsView over the canvas root and calls done() on
   *  OK/CANCEL. Absent (a headless harness) ⇒ no UI shown. DISPLAY-only.
   *  The view receives the pure GameOptionsModel (the read/apply built off shellEnv). */
  domGameOptions?: (model: GameOptionsModel, done: () => void) => void;
  /** The pause-menu FULL-manual HELP
   *  DOM-overlay seam threaded into each GameSession (gameSessionOpts.domHelpManual).
   *  main.ts mounts a HelpManualOverlayView over the canvas root and calls done() on
   *  OK/Esc. DISTINCT from the build panels' SECTION-only domHelp. Absent (a headless
   *  harness) ⇒ no UI shown. DISPLAY-only. */
  domHelpManual?: (done: () => void) => void;
  /** The in-mission E-COM (Form 6)
   *  overlay DOM-overlay seam threaded into each GameSession (gameSessionOpts.domEcom).
   *  main.ts mounts an EcomView over the canvas root and calls done() on dismiss.
   *  The browser-timer typewriter animates under the SP story-modal pause. Absent
   *  (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domEcom?: (info: EcomInfo, done: () => void) => void;
  /** The in-mission (kidfCutScene 1047)
   *  CUTSCENE player DOM-overlay seam threaded into each GameSession
   *  (gameSessionOpts.domCutScene). main.ts mounts a CutSceneView over the canvas root
   *  and calls done() on dismiss. The browser-timer typewriter animates under the SP
   *  story-modal pause. Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domCutScene?: (info: CutSceneInfo, done: () => void) => void;
  /** "is a DOM full-screen story overlay
   *  (the in-mission CUTSCENE) still mounted?" — threaded into each GameSession
   *  (gameSessionOpts.domStoryOverlayActive) so WireJumpToMission's hand-off waits for
   *  the s_00 intro overlay to clear before chaining to s_01 (else the s_01 briefing
   *  E-Com overlaps the intro). Absent (a headless harness) ⇒ no UI shown.
   *  SEQUENCING-only. */
  domStoryOverlayActive?: () => boolean;
  /** The HtMessageBox family
   *  (alerts / warnings / errors / Yes-No queries) DOM-overlay seam threaded into each
   *  GameSession (gameSessionOpts.domMessageBox → shellEnv.domMessageBox). main.ts mounts
   *  a MessageBoxView over the canvas root; done(fOk) routes to the SAME callback the
   *  canvas HtMessageBox DoModal uses (the resign/leave WARNING, the GAME OVER observe
   *  query, MP comm/server-message errors — all wire-identical, no Message/cmdq). Absent
   *  (a headless harness) ⇒ no UI shown. DISPLAY-only — no sim/command/wire change. */
  domMessageBox?: (
    title: string,
    message: string,
    variant: 'ok' | 'query',
    whiteBorder: boolean,
    done: (fOk: boolean) => void,
  ) => void;
  /** The in-game MP "LAG ALERT" (forms.ini-elim)
   *  Yes/No query (SimUI.cpp OnLagNotify) DOM-overlay seam threaded into each GameSession
   *  (gameSessionOpts.domLagBox → shellEnv.domLagBox). DEDICATED (not the shared domMessageBox
   *  singleton) because the lag box is MODELESS/independent — it can be up while a resign box
   *  shows and is hidden/re-shown for a different lagger. main.ts mounts an independent
   *  MessageBoxView; the box reports Yes/No through done(fOk) and returns its teardown closure.
   *  Absent (a headless harness) ⇒ no UI shown. */
  domLagBox?: (title: string, body: string, done: (fOk: boolean) => void) => () => void;
  /** The in-game LOAD / SAVE slot-list overlay
   *  + the MP "WAITING FOR ALL PLAYERS…" overlay, threaded into each GameSession
   *  (gameSessionOpts.domLoadGame/domSaveGame/domWaiting). main.ts mounts a LoadSaveView /
   *  WaitingView over the canvas. Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domLoadGame?: (store: SaveStore, done: (slot: number | null) => void) => void;
  domSaveGame?: (store: SaveStore, done: (slot: number | null) => void) => void;
  domWaiting?: (title: string) => () => void;
  /** The in-game (build/order STEP 1)
   *  kidfAreYouSure CONFIRMATION DOM-overlay seam threaded into each GameSession
   *  (gameSessionOpts.domConfirm → BuildUIDeps). main.ts mounts a ConfirmView over the
   *  canvas root; onResult routes to the SAME continuation BuildUI.PopupConfirmation
   *  uses (YES → the existing command action; NO → nothing). COMMAND-ADJACENT,
   *  DISPLAY-only — no command/Message is constructed in the DOM. Absent (a headless
   *  harness) ⇒ no UI shown. */
  domConfirm?: (
    title: string,
    titleColor: string,
    anchor: { cx: number; cy: number } | null,
    onResult: (confirmed: boolean) => void,
  ) => void;
  /** The SECTION-ONLY in-game HELP DOM overlay
   *  threaded into each GameSession (gameSessionOpts.domHelp → BuildUIDeps). The build panels'
   *  "?" (kidcHelp) calls this with the context section anchor (buildings / vehicles /
   *  personnel / upgrades); main.ts mounts a HelpView OVER the build panel (the panel stays
   *  open underneath) that fetches /help.txt, extracts that section, and renders it (close X /
   *  Esc / backdrop → onClose returns to the build panel). DISPLAY-only — no command/Message.
   *  Mirrors domConfirm. Absent (a headless harness) ⇒ no UI shown. */
  domHelp?: (anchor: string) => void;
  /** The in-game gob (build/order STEP 2)
   *  CONTEXT MENU (UnitMenu) DOM-overlay seam threaded into each GameSession
   *  (gameSessionOpts.domUnitMenu → BuildUIDeps). main.ts mounts a UnitMenuView anchored
   *  to the gob; the view re-polls getModel() (the SAME InitMenu/SetOwner logic) on a
   *  ~25ms cadence so the buttons track the gob state + the anchor follows the map, and
   *  onSelect(idc) routes to the SAME OnMenuItemSelected BuildUI.PopupMenu uses (Build →
   *  the build panel, Sell/Abort → domConfirm, Repair/Research/Transform/Deliver →
   *  enqueue). COMMAND-ADJACENT, DISPLAY-only — no command/Message in the DOM. Absent
   *  (a headless harness) ⇒ no UI shown. */
  domUnitMenu?: (
    getModel: () => UnitMenuModel | null,
    onSelect: (idc: number) => void,
    onClose: () => void,
  ) => void;
  /** The in-game UNIT (build/order STEP 3)
   *  BUILD carousel (MobileUnitBuildForm — VTS "ORDER VEHICLE" / HRC "RECRUIT PERSONNEL")
   *  DOM-overlay seam threaded into each GameSession (gameSessionOpts.domBuildPanel →
   *  BuildUIDeps). main.ts mounts a BuildPanelView; the view re-polls getModel() (the SAME
   *  SetOwner/UpdateUnitInfo/UpdateOrderButton logic) so the queue counts + the yellow
   *  progress bar + the limit label refresh live, and onOrder()/onCancelOrder() route to
   *  the SAME OnControlSelected BuildUI runs (the EXACT command enqueue stays in buildui.ts).
   *  COMMAND-ADJACENT, DISPLAY-only — no command/Message in the DOM. Absent (a headless
   *  harness) ⇒ no UI shown. */
  domBuildPanel?: (
    getModel: () => BuildPanelModel | null,
    onSelect: (ut: number) => void,
    onOrder: () => void,
    onCancelOrder: () => void,
    onClose: () => void,
    onHelp: () => void,
  ) => void;
  /** The in-game (build/order STEP 3, FORM C)
   *  STRUCTURE BUILD carousel (StructureBuildForm — kidfBuildStructure "ORDER BUILDING")
   *  DOM-overlay seam threaded into each GameSession (gameSessionOpts.domStructurePanel →
   *  BuildUIDeps). main.ts mounts the SAME BuildPanelView (structure variant); the view
   *  re-polls getModel() (the SAME SetOwner/UpdateStructureInfo logic); onBuild() runs
   *  "Build" — which ENQUEUES NOTHING: it returns the selected gt and BuildUI hands it to
   *  gpfrmPlace (the CANVAS placement overlay, FORM F not ported). DISPLAY-only — no
   *  command/Message in the DOM. Absent (a headless harness) ⇒ no UI shown. */
  domStructurePanel?: (
    getModel: () => BuildPanelModel | null,
    onSelect: (ut: number) => void,
    onBuild: () => void,
    onClose: () => void,
    onHelp: () => void,
  ) => void;
  /** The in-game RESEARCH carousel (FORM E)
   *  (UpgradeForm — kidfUpgrade "R&D TECHNOLOGIES") DOM-overlay seam threaded into each
   *  GameSession (gameSessionOpts.domUpgradePanel → BuildUIDeps). main.ts mounts the SAME
   *  BuildPanelView (research variant — ONLY a COST meter + PREREQUISITES + DESCRIPTION, an
   *  empty state); the view re-polls getModel() (the SAME SetOwner/UpdateUpgradeInfo logic);
   *  onResearch() runs "Research" — which ENQUEUES NOTHING: it returns the selected upgm and
   *  the BuildUI continuation enqueues kmidUpgradeCommand (E-R5). DISPLAY-only — no
   *  command/Message in the DOM. Absent (a headless harness) ⇒ no UI shown. */
  domUpgradePanel?: (
    getModel: () => BuildPanelModel | null,
    onSelect: (upgm: number) => void,
    onResearch: () => void,
    onClose: () => void,
    onHelp: () => void,
  ) => void;
  /** The persistent in-game HUD (Form 5)
   *  BOTTOM BAR DOM-overlay seam threaded into each GameSession (gameSessionOpts.domHud).
   *  When set, InputUIForm hides its OWN bottom-bar display controls + pushes the live
   *  {credits, needCredits, powerDemand, powerSupply} here per frame. main.ts mounts a
   *  persistent HudView over the live canvas (pointer-events:none container — map
   *  gestures fall through). Absent (a headless harness) ⇒ no UI shown. DISPLAY-only. */
  domHud?: {
    update(state: {
      credits: number;
      needCredits: number;
      powerDemand: number;
      powerSupply: number;
      demandMax: number;
      flashOn: boolean;
    }): void;
  };
  /** The in-game OVERLAY-TEXT-LABEL DOM
   *  seam threaded into each GameSession (gameSessionOpts.domSimUI). When set, the
   *  SimUIForm suppresses the canvas paint of its 4 text labels (objective / alert /
   *  countdown / FPS) + pushes a model here per frame; main.ts mounts a SimUIView over
   *  the live canvas (pointer-events:none — map gestures fall through). Absent (a
   *  headless harness) ⇒ no UI shown. DISPLAY-only. */
  domSimUI?: { update(m: SimUIModel): void };
}

// TS-only reconnect tuning (tasks/reconnect-resume-plan.md). Budget < the DO's
// 30s grace; per-attempt wait short (fast-fail → retry); backoff caps the gap.
const kReconnectBudgetMs = 25_000;
const kReconnectWaitMs = 5_000;
const kReconnectMaxBackoffMs = 4_000;

// knGoSuccess = win; knGoAbortLevel = the player quit to the menu; everything
// else (knGoRetryLevel / the replay default) is a loss the player could retry.
const spOutcome = (nGo: number): 'win' | 'lose' | 'abort' =>
  nGo === knGoSuccess ? 'win' : nGo === knGoAbortLevel ? 'abort' : 'lose';

// SP live-presence heartbeat cadence — drives the dashboard's "SP players now".
const kSpHeartbeatMs = 30000;
const spHcapLabel = (wf: number): 'easy' | 'normal' | 'hard' =>
  wf === kfHcapEasy ? 'easy' : wf === kfHcapHard ? 'hard' : 'normal';

const cloneMiid = (m: MissionIdentifier): MissionIdentifier => ({
  packid: { id: m.packid.id, hash: m.packid.hash.slice() },
  szLvlFilename: m.szLvlFilename,
});

const snapshotPvars = (gs: GameSession): PvarEntry[] =>
  gs.sim.ctx.pvars.entriesInOrder().map((e) => ({ name: e.name, value: e.value }));

export class GameHost {
  private shell!: ShellSession;
  private game: RunningGame | null = null;
  /** gpstmSavedGame — the in-game "Load" handoff (set by the session's menuEnv,
   *  consumed by the menu's takeSavedGameStream on the knGoLoadSavedGame reload). */
  savedGameStream: Stream | null = null;
  private lastMiid: MissionIdentifier | null = null;
  readonly mpStats: MpStats = { live: false, reports: 0, syncErrors: 0, hashes: [] };
  /** B4b: the wss:// proxy of the connected server (for the host's "Add Bot"). */
  connectedWebsocketProxy = '';

  private readonly deps: GameHostDeps;
  /** SP live-presence heartbeat timer (null when no mission is running). Rides the
   *  SAME fire-and-forget spEvent path; cleared the instant the mission resolves. */
  private spHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** DOM-menu mode (Phase 2, ?dom): an external HTML menu is the visible menu,
   *  so pump must not tick the (hidden) canvas ShellSession when no game runs.
   *  The shell stays attached — it still backs gameSessionOpts' prefs/options/
   *  sndm/gtGameSpeed — it just isn't ticked or shown. */
  private externalMenu = false;

  constructor(deps: GameHostDeps) {
    this.deps = deps;
  }

  /** Wire the menu the host drives (broken out of construction because the
   *  menu's flow forwards into this host — a forward reference both ways). */
  attach(shell: ShellSession): void {
    this.shell = shell;
  }

  /** Switch to DOM-menu mode: stop ticking the canvas menu when no game runs. */
  useExternalMenu(): void {
    this.externalMenu = true;
  }

  // ── what the browser glue (present/pointer/resize/wiTs) reads ───────────────

  /** The session that owns the screen right now (game if running, else menu). */
  currentSession(): ShellSession | GameSession {
    return this.game?.gs ?? this.shell;
  }
  /** The running game session, or null at the menu. */
  runningGame(): GameSession | null {
    return this.game?.gs ?? null;
  }
  inGame(): boolean {
    return this.game !== null;
  }
  /** issue #110: a SINGLE-PLAYER game is running — the hidden-page pause is
   *  allowed (an MP game must keep servicing lockstep while hidden, so the
   *  shell's hidden sim-pause + audio-suspend apply only when this is true). */
  inSinglePlayerGame(): boolean {
    return this.game !== null && !this.mpStats.live;
  }
  lastMissionIdentifier(): MissionIdentifier | null {
    return this.lastMiid;
  }

  /** menuEnv seam: the in-game "Load" arms hand the host the chosen stream. */
  setSavedGameStream(pstm: Stream): void {
    this.savedGameStream = pstm;
  }
  /** envExtras seam: the menu consumes the stashed stream on the reload. */
  takeSavedGameStream(): Stream | null {
    const pstm = this.savedGameStream;
    this.savedGameStream = null;
    return pstm;
  }

  // ── the pump (the rAF tick, dispatched by the host's frame loop) ────────────

  /** game/game.cpp's frame: advance the running game; on game-over sync the
   *  one gtGameSpeed global back to the menu and report the outcome (which may
   *  start the next session or resume the menu). With no game, tick the menu. */
  pump(n: number): void {
    if (this.game !== null) {
      const g = this.game;
      g.gs.advanceTicks(n);
      if (g.gs.fGameOver) {
        this.game = null;
        this.shell.gtGameSpeed = g.gs.gtGameSpeed; // one gtGameSpeed in the C++
        g.onGo(g.gs.nGo);
      }
    } else if (!this.externalMenu) {
      this.shell.advanceTicks(n); // DOM-menu mode: nothing to tick (the HTML menu is event-driven)
    }
  }

  // ── session opts (the menu's live globals ride into each GameSession) ───────

  private runGame(gs: GameSession, onGo: (nGo: number) => void): void {
    this.game = { gs, onGo };
  }

  private gameSessionOpts(miid: MissionIdentifier) {
    const d = this.deps;
    return {
      pakr: d.pakr,
      tam: d.tam,
      coords: d.coords,
      gameIni: d.gameIni,
      screen: d.screen,
      createSurface: d.createSurface,
      sndm: this.shell.sndm,
      prefs: this.shell.prefs,
      prefStore: d.prefStore,
      optionsState: this.shell.options,
      gtGameSpeed: this.shell.gtGameSpeed,
      hostGetCurrentKeyState: d.hostGetCurrentKeyState,
      saveStore: d.saveStore,
      completeMgr: d.completeMgr,
      miid,
      // ?dom: route the in-game OBJECTIVES briefing to the DOM overlay (Phase 5).
      domShowObjectives: d.domShowObjectives,
      // ?dom: route the in-game PAUSE MENU to the DOM overlay (Phase 5, Form 2).
      domInGameMenu: d.domInGameMenu,
      // ?dom: route the WIN/LOSE RESULTS SUMMARY to the DOM overlay (Phase 5, Form 3).
      domObjectivesSummary: d.domObjectivesSummary,
      // MP: light up the end-of-game summary's "VIEW REPLAY" when the server announces the recording.
      onReplayAvailable: d.onReplayAvailable,
      // ?dom: route the in-game GAME OPTIONS to the DOM overlay (Phase 5, Form 4).
      domGameOptions: d.domGameOptions,
      // ?dom: route the pause-menu FULL-manual HELP to the DOM overlay (Phase 5).
      domHelpManual: d.domHelpManual,
      // ?dom: route the in-mission E-COM overlay to the DOM overlay (Phase 5, Form 6).
      domEcom: d.domEcom,
      // ?dom: route the in-mission CUTSCENE player to the DOM overlay (Phase 5, 1047).
      domCutScene: d.domCutScene,
      // ?dom: WireJumpToMission's DOM guard — wait for the intro overlay to clear before
      // the s_00→s_01 hand-off (so the s_01 briefing E-Com doesn't overlap the intro).
      domStoryOverlayActive: d.domStoryOverlayActive,
      // ?dom: route the HtMessageBox family (alerts/warnings/errors/queries) to the DOM
      // overlay (Phase 5, survey I10) — incl. the resign/leave WARNING + observe query.
      domMessageBox: d.domMessageBox,
      // ?dom: route the MP "LAG ALERT" Yes/No to a DEDICATED DOM overlay (forms.ini-elim) —
      // the last in-game always-on forms.ini consumer (the modeless lag CreateHtMessageBox).
      domLagBox: d.domLagBox,
      domLoadGame: d.domLoadGame,
      domSaveGame: d.domSaveGame,
      domWaiting: d.domWaiting,
      // ?dom: route the kidfAreYouSure CONFIRMATION to the DOM overlay (Phase 5, STEP 1).
      domConfirm: d.domConfirm,
      // ?dom: route the build panels' "?" HELP (section-only) to the DOM overlay (Phase 5).
      domHelp: d.domHelp,
      // ?dom: route the gob CONTEXT MENU (UnitMenu) to the DOM overlay (Phase 5, STEP 2).
      domUnitMenu: d.domUnitMenu,
      // ?dom: route the UNIT BUILD carousel (VTS/HRC) to the DOM overlay (Phase 5, STEP 3).
      domBuildPanel: d.domBuildPanel,
      // ?dom: route the STRUCTURE BUILD carousel (HQ) to the DOM overlay (Phase 5, FORM C).
      domStructurePanel: d.domStructurePanel,
      // ?dom: route the RESEARCH carousel (research center) to the DOM overlay (Phase 5, FORM E).
      domUpgradePanel: d.domUpgradePanel,
      // ?dom: push the persistent HUD bottom bar (Phase 5, Form 5).
      domHud: d.domHud,
      // ?dom: push the in-game overlay text labels (objective / alert / countdown / FPS).
      domSimUI: d.domSimUI,
      menuEnv: {
        // InputUI.cpp:380 / objectives.ts:275 — gpstmSavedGame = pstm
        setSavedGameStream: (pstm: Stream) => {
          this.savedGameStream = pstm;
        },
      },
    };
  }

  // game/Level.cpp:207-214 — [General] TileMap
  private tileMapNameFor(szLvlFilename: string): string {
    const ld = LevelDataFromPack(this.deps.pakr, szLvlFilename);
    for (const s of ld.sections) {
      if (s.name !== 'General') continue;
      for (const p of s.props) {
        if (p.key === 'TileMap') return p.value;
      }
    }
    throw new Error(`tileMapNameFor: ${szLvlFilename} has no [General] TileMap`);
  }

  // ── SP live-presence heartbeat ──────────────────────────────────────────────
  // While a mission runs, send a tiny 'alive' beat (current level + elapsed +
  // difficulty) every ~30s. The whole tick is try-wrapped over the fire-and-forget
  // spEvent path, so it can NEVER disrupt gameplay; the timer is cleared the instant
  // the mission ends (no zombie) and server-side stale presence ages out on its own.
  private spHeartbeatStop(): void {
    if (this.spHeartbeatTimer !== null) {
      clearInterval(this.spHeartbeatTimer);
      this.spHeartbeatTimer = null;
    }
  }
  private spHeartbeatStart(mission: string, pack: number, startMs: number): void {
    this.spHeartbeatStop(); // never stack timers across mission transitions
    const d = this.deps;
    this.spHeartbeatTimer = setInterval(() => {
      try {
        d.spEvent({
          t: 'alive',
          mission,
          pack,
          secs: Math.round((Date.now() - startMs) / 1000),
          hcap: spHcapLabel(this.shell?.options?.wfHandicap ?? 0),
        });
      } catch {
        /* a heartbeat must never surface to the player */
      }
    }, kSpHeartbeatMs);
  }

  // ── single-player launch (the seam) ─────────────────────────────────────────

  // game/game.cpp:1577-1736 Game::PlayLevel — the do-while over RunSimulation:
  // next-level chain (SetNextMissionAction), retry (pvars revert to the level
  // entry snapshot), pvar carry-forward on success, MarkComplete on transition.
  // Each iteration mounts the mission's pack pdb (game.cpp:1635-1642) and
  // unmounts it after the session ends (game.cpp:1674) — no-ops for the main
  // pack, the resolution path for DLC packs. fMounted0: the saved-game arm
  // (launchSavedGame) already holds the first mount.
  private playMissionLoop(
    miid0: MissionIdentifier,
    gs0: GameSession | null,
    pvars0: PvarEntry[],
    done: (nGo: number) => void,
    fMounted0 = false,
  ): void {
    const d = this.deps;
    let miid = cloneMiid(miid0);
    let pvarsEntry = pvars0;
    let fMounted = fMounted0;
    this.lastMiid = miid;

    const startFresh = (): GameSession | null => {
      if (!d.packm.Mount(d.pakr, miid.packid)) return null; // game.cpp:1635-1642
      fMounted = true;
      try {
        return PlayLevelSession(
          {
            miid,
            wfHandicap: this.shell.options.wfHandicap,
            templates: d.templates,
            anir: d.anir,
            pvars: pvarsEntry,
          },
          this.gameSessionOpts(miid),
        );
      } catch (e) {
        console.error('PlayLevelSession failed:', e);
        d.packm.Unmount(d.pakr, miid.packid);
        fMounted = false;
        return null;
      }
    };

    const run = (gs: GameSession | null): void => {
      if (gs === null) {
        done(knGoInitFailure);
        return;
      }
      // SP funnel telemetry: one 'start' per mission attempt; the matching 'end'
      // fires below on a real play outcome. Snapshot the identity now — miid
      // mutates on next-level.
      const spMission = miid.szLvlFilename;
      const spPack = miid.packid.id >>> 0;
      const spStartMs = Date.now();
      d.spEvent({
        t: 'start',
        mission: spMission,
        pack: spPack,
        hcap: spHcapLabel(this.shell.options.wfHandicap),
      });
      this.spHeartbeatStart(spMission, spPack, spStartMs); // live presence until this mission resolves
      this.runGame(gs, (nGo) => {
        this.spHeartbeatStop(); // mission resolved (win/lose/abort/app-transition) — stop the heartbeat
        if (fMounted) {
          // game.cpp:1674 — gppackm->Unmount after RunSimulation
          d.packm.Unmount(d.pakr, miid.packid);
          fMounted = false;
        }
        // Emit the outcome for real play-resolutions only — app-stop /
        // init-failure / load-saved are app transitions, not mission outcomes.
        if (nGo !== knGoAppStop && nGo !== knGoInitFailure && nGo !== knGoLoadSavedGame) {
          d.spEvent({
            t: 'end',
            mission: spMission,
            pack: spPack,
            outcome: spOutcome(nGo),
            secs: Math.round((Date.now() - spStartMs) / 1000),
          });
        }
        switch (nGo) {
          case knGoAppStop:
          case knGoInitFailure:
          case knGoLoadSavedGame:
            done(nGo); // game.cpp:1678-1680
            return;

          case knGoSuccess:
            if (gs.szNextLevel === '') {
              done(knGoAbortLevel);
              return;
            } // game.cpp:1686
            this.deps.completeMgr.MarkComplete(miid); // game.cpp:1704-1717
            miid = { packid: miid.packid, szLvlFilename: gs.szNextLevel };
            this.lastMiid = miid;
            pvarsEntry = snapshotPvars(gs); // dictPvarsRetry.Init(&m_dictPvars)
            run(startFresh());
            return;

          case knGoRetryLevel:
            run(startFresh()); // pvars revert to the entry snapshot (game.cpp:1722-1724)
            return;

          case knGoAbortLevel:
            done(knGoAbortLevel);
            return;

          default:
            pvarsEntry = []; // clear pvars (game.cpp:1727-1732), replay the level
            run(startFresh());
            return;
        }
      });
    };

    run(gs0 ?? startFresh());
  }

  /** ggame.PlayLevel — Shell::PlayGame's kpmNormal arm (the playLevel seam). */
  launchSinglePlayer(pmiid: MissionIdentifier, _nRank: number, done: (nGo: number) => void): void {
    this.playMissionLoop(pmiid, null, [], done);
  }

  /** ggame.PlaySavedGame — the envelope load (game.cpp:1583-1601) + the loop. */
  launchSavedGame(pstm: Stream, done: (nGo: number) => void): void {
    const d = this.deps;
    const env = PlaySavedGame(pstm); // closes the stream when stale
    if (env === null) {
      done(knGoInitFailure);
      return;
    }
    const ms = pstm as unknown as { bytes(): Uint8Array; tell(): number };
    if (typeof ms.bytes !== 'function') {
      pstm.Close();
      done(knGoInitFailure);
      return;
    }
    const tail = ms.bytes().subarray(ms.tell());
    pstm.Close();

    // game.cpp:1635-1642 — the saved mission's pack must be mounted before the
    // level-data load; playMissionLoop takes the mount over
    if (!d.packm.Mount(d.pakr, env.miid.packid)) {
      done(knGoInitFailure);
      return;
    }

    let gs: GameSession | null = null;
    let pvars: PvarEntry[] = [];
    try {
      const sim = Sim.load({
        save: tail,
        levelDataFor: (szLevel: string) => LevelDataFromPack(d.pakr, szLevel),
        templates: d.templates,
        anir: d.anir,
        multiplayer: false,
        host: clientFogHost(),
      });
      pvars = sim.ctx.pvars.entriesInOrder().map((e) => ({ name: e.name, value: e.value }));
      gs = new GameSession({
        ...this.gameSessionOpts(env.miid),
        sim,
        templates: d.templates,
        tmapName: this.tileMapNameFor(env.miid.szLvlFilename),
      });
      WireJumpToMission(gs); // TriggerActions.cpp:1229-1261 (see playlevel.ts)
      gs.szNextLevel = env.szNextLevel; // game.cpp:1592
    } catch (e) {
      console.error('PlaySavedGame failed:', e);
    }
    if (gs === null) {
      d.packm.Unmount(d.pakr, env.miid.packid);
      done(knGoInitFailure);
      return;
    }
    this.playMissionLoop(env.miid, gs, pvars, done, true);
  }

  // ── multiplayer launch (the seam) ────────────────────────────────────────────

  // ggame.RunSimulation's MP arm (lobby.cpp:158-177 → game.cpp:1738+): build the
  // lockstep MP Sim + GameSession off the staged begin info and hand it to the
  // SAME pump the SP sessions ride; done(nGo) is the C++ return. The pack is
  // already mounted (Lobby::BeginGame mounts gameinfo.params.packid around this).
  runSimulation(info: BeginGameInfo, done: (nGo: number) => void): void {
    const d = this.deps;
    let gs: GameSession | null = null;
    try {
      const lvl = (info.serverParams ?? info.gameinfo.params).szLvlFilename;
      const sim = Sim.create({
        levelName: lvl,
        levelData: LevelDataFromPack(d.pakr, lvl),
        templates: d.templates,
        anir: d.anir,
        seed: info.seed | 0,
        roster: info.roster,
        multiplayer: true,
        host: clientFogHost(),
      });
      const miid: MissionIdentifier = {
        packid: {
          id: info.gameinfo.params.packId,
          hash: info.gameinfo.params.packHash.slice(),
        },
        szLvlFilename: lvl,
      };
      gs = new GameSession({
        ...this.gameSessionOpts(miid),
        sim,
        templates: d.templates,
        tmapName: this.tileMapNameFor(lvl),
        wfRole: info.wfRole,
        mpTransport: info.tra,
        gameid: info.gameinfo.gameid,
        chatter: info.chatter,
        onReconnectNeeded: () => {
          void reconnect();
        }, // mid-game drop → re-open/re-login/resume
      });
      // the harness's lockstep observability (block-hash reports, sync errors)
      const chain = sim.onBlockHash;
      sim.onBlockHash = (b, h) => {
        chain(b, h);
        this.mpStats.reports++;
        this.mpStats.hashes.push([b, h]);
      };
    } catch (e) {
      console.error('MP RunSimulation failed:', e);
    }
    if (gs === null) {
      done(knGoInitFailure);
      return;
    }
    const s = gs;

    let live = info.tra; // the active transport — re-pointed after a reconnect
    let reconnecting = false;

    // SimUIForm::Init (SimUI.cpp:276-278): the in-game form takes the transport
    // callbacks. Factored so the SAME wiring re-attaches to the fresh transport
    // after a reconnect. A mid-game socket close routes to s.OnConnectionClose(),
    // which — if reconnect is viable — fires onReconnectNeeded instead of ending.
    const wire = (tra: XTransport): void => {
      tra.SetCallback({
        OnStatusUpdate: () => {},
        OnConnectionClose: () => {
          s.OnConnectionClose();
        },
        OnShowMessage: (m: string) => {
          s.OnShowMessage(m);
        },
      });
      tra.SetGameCallback({
        OnReceiveChat: (player: string, chat: string) => s.OnReceiveChat(player, chat),
        OnNetMessage: (nm) => {
          if (nm.nmid === NMID.ScSyncError) this.mpStats.syncErrors++;
          s.OnNetMessage(nm);
        },
        OnGameDisconnect: () => s.OnGameDisconnect(),
      });
    };

    // TS-only reconnect: the live socket dropped mid-game (the DO holds our slot).
    // Re-open a FRESH transport, re-login (same did → the DO matches the held
    // slot), and CsResumeGame from our current block; on success re-point the
    // session at the new transport. Retry with backoff until the budget runs out,
    // then fall back to the legacy abort-to-menu.
    const reconnect = async (): Promise<void> => {
      if (reconnecting || s.fGameOver) return;
      reconnecting = true;
      const deadline = Date.now() + kReconnectBudgetMs;
      for (let attempt = 0; !s.fGameOver && Date.now() < deadline; attempt++) {
        const tra = new XTransport({
          connect: webSocketConnector(this.connectedWebsocketProxy),
          deviceId: this.shell.prefs.GetString(kszPrefDeviceId),
          platform: 'web',
          clientId: CLIENT_TS,
          waitMs: kReconnectWaitMs,
        });
        try {
          await tra.Open();
          if (tra.state() === XTS.OPEN) {
            await tra.Login(info.handler.username(), info.handler.token());
            if (tra.state() === XTS.LOGGEDIN) {
              wire(tra); // route the catch-up frames to the session before resume
              if (await tra.ResumeGame(info.gameinfo.gameid, s.resumeBlockPoint())) {
                live = tra;
                d.mp.tra = tra;
                s.onReconnected(tra);
                reconnecting = false;
                return;
              }
              tra.Close(); // the DO rejected resume (slot gone / grace expired)
              break;
            }
          }
          tra.Close();
        } catch {
          try {
            tra.Close();
          } catch {
            /* ignore */
          }
        }
        await new Promise((r) =>
          setTimeout(r, Math.min(kReconnectMaxBackoffMs, 250 * 2 ** attempt)),
        );
      }
      reconnecting = false;
      if (!s.fGameOver) s.abortReconnect();
    };

    wire(info.tra);
    this.mpStats.live = true;

    this.runGame(s, (nGo) => {
      // ~SimUIForm / RunSimulation epilogue: release the (possibly reconnected)
      // transport callbacks, tear the session down, resolve the seam.
      this.mpStats.live = false;
      live.SetGameCallback(null);
      live.SetCallback(null);
      s.Dispose();
      done(nGo);
    });
  }

  // ── the Lobby::Shell global reaches (lobby.cpp over the browser host) ─────────

  lobbyDeps(): LobbyShellDeps {
    const d = this.deps;
    const mlDeps: MissionListDeps = { pakr: d.pakr, packm: d.packm, ppim: d.ppim, fDemo: false };
    const deviceId = this.shell.prefs.GetString(kszPrefDeviceId); // gszDeviceId (game.cpp:316)
    return {
      env: this.shell.env,
      mp: d.mp,
      chatc: d.chatc,
      httpGet: d.httpGet,
      deviceId,
      platform: 'web', // HostGetPlatformString (the web build arm)
      serverInfoUrl: d.serverInfoUrl, // ?api override; undefined = prod
      envBeta: d.envBeta,
      makeTransport: (info) => {
        this.connectedWebsocketProxy = info.websocket_proxy; // B4b: remember the DO (for addBot)
        return new XTransport({
          connect: webSocketConnector(info.websocket_proxy),
          deviceId,
          platform: 'web',
          clientId: CLIENT_TS, // advertise reconnect-capability so the DO holds our slot
        });
      },
      cgDeps: { mlDeps, completeMgr: d.completeMgr },
      runSimulation: (info, done) => {
        this.runSimulation(info, done);
      },
    };
  }
}
